import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiLoader, FiLogIn, FiUserPlus, FiUsers, FiX } from 'react-icons/fi';
import { BsSuitClub } from 'react-icons/bs';
import { socialApi } from '../../../supportApi';
import { buildSigninUrl } from '../../auth/guestAuth';
import { clubBusyKeyFromSlugAndId } from '../clubs/clubListUtils';
import { loadStarterClubCatalog } from '../../config/starterClubCatalog';
import {
  collectSuggestionClubs,
  collectSuggestionPeople,
  interleaveSuggestionItems,
  mergeSuggestionPeople,
} from '../../lib/feedSuggestions';
import {
  contributorAvatarSrc,
  formatCompactCount,
  resolveDiscussionAssetUrl,
} from '../../lib/discussionUtils';
import { getUniqueDefaultAvatarDataUrl } from '../../../../../utils/uniqueDefaultAvatar';
import { normalizeProfileMode, resolveViewerFollowMode } from '../../../../profileIdentity/activeProfileMode';

const CARD_SCROLL_PAD = 8;
const SUGGEST_TABS = [
  { id: 'all', label: 'All', ariaLabel: 'Suggested for you' },
  { id: 'people', label: 'People', ariaLabel: 'Suggested people' },
  { id: 'clubs', label: 'Clubs', ariaLabel: 'Suggested clubs' },
];

function avatarFallback(userId, name) {
  return getUniqueDefaultAvatarDataUrl({ userId, name });
}

function SuggestionCardChrome({ action, onDismiss, dismissLabel }) {
  return (
    <div className="od-suggest-card-top">
      <div className="od-suggest-card-top-start">{action}</div>
      <button
        type="button"
        className="od-suggest-card-dismiss noOutline-text"
        aria-label={dismissLabel}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <FiX aria-hidden />
      </button>
    </div>
  );
}

function SuggestionIconAction({ label, disabled, busy, onClick, children }) {
  return (
    <button
      type="button"
      className={`od-suggest-card-icon-btn noOutline-text${busy ? ' is-busy' : ''}`}
      aria-label={label}
      disabled={disabled || busy}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {busy ? <FiLoader className="od-suggest-card-icon-btn-spin" aria-hidden /> : children}
    </button>
  );
}

function SuggestionPersonCard({
  item,
  loggedIn,
  followState,
  followBusy,
  onFollow,
  onOpen,
  onDismiss,
  onSignIn,
}) {
  const src = contributorAvatarSrc(item.profilePictureUrl, process.env.REACT_APP_BASE_URL || '', item.profileMode, {
    userId: item.userId,
    name: item.name,
  });
  const status = followState?.status;
  const showAction = !loggedIn || (status !== 'active' && status !== 'self');
  const pending = status === 'pending';

  const action = showAction ? (
    loggedIn ? (
      <SuggestionIconAction
        label={followBusy ? 'Updating follow' : pending ? 'Follow requested' : `Follow ${item.name}`}
        disabled={pending}
        busy={followBusy}
        onClick={() => onFollow(item)}
      >
        <FiUserPlus aria-hidden />
      </SuggestionIconAction>
    ) : (
      <SuggestionIconAction label="Sign in to follow" onClick={onSignIn}>
        <FiLogIn aria-hidden />
      </SuggestionIconAction>
    )
  ) : null;

  return (
    <article className="od-suggest-card od-suggest-card--person">
      <div className="od-suggest-card-media">
        <img
          src={src}
          alt=""
          className="od-suggest-card-media-img"
          onError={(e) => {
            const fallback = avatarFallback(item.userId, item.name);
            if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
          }}
        />
        <div className="od-suggest-card-media-chrome od-suggest-card-media-chrome--top">
          <SuggestionCardChrome
            action={action}
            dismissLabel={`Hide ${item.name}`}
            onDismiss={() => onDismiss(item.id)}
          />
        </div>
        <button type="button" className="od-suggest-card-caption noOutline-text" onClick={() => onOpen(item)}>
          <strong className="od-suggest-card-name">{item.name}</strong>
          <span className="od-suggest-card-meta">Might be worth knowing</span>
        </button>
      </div>
    </article>
  );
}

function SuggestionClubCard({ item, loggedIn, joinBusy, onJoin, onOpen, onDismiss, onSignIn }) {
  const thumb = item.thumbnailUrl ? resolveDiscussionAssetUrl(item.thumbnailUrl) : '';
  const members = formatCompactCount(item.memberCount);
  const alreadyIn = !!item.joined;
  const showAction = !alreadyIn;

  const action = showAction ? (
    loggedIn ? (
      <SuggestionIconAction
        label={joinBusy ? 'Joining club' : `Join ${item.name}`}
        busy={joinBusy}
        onClick={() => onJoin(item)}
      >
        <FiUsers aria-hidden />
      </SuggestionIconAction>
    ) : (
      <SuggestionIconAction label="Sign in to join" onClick={onSignIn}>
        <FiLogIn aria-hidden />
      </SuggestionIconAction>
    )
  ) : null;

  return (
    <article className="od-suggest-card od-suggest-card--club">
      <div
        className={`od-suggest-card-media${thumb ? '' : ' od-suggest-card-media--placeholder od-suggest-card-media--club'}`}
      >
        {thumb ? (
          <img src={thumb} alt="" className="od-suggest-card-media-img" />
        ) : (
          <BsSuitClub className="od-suggest-card-media-placeholder-icon" aria-hidden />
        )}
        <div className="od-suggest-card-media-chrome od-suggest-card-media-chrome--top">
          <SuggestionCardChrome
            action={action}
            dismissLabel={`Hide ${item.name}`}
            onDismiss={() => onDismiss(item.id)}
          />
        </div>
        <button type="button" className="od-suggest-card-caption noOutline-text" onClick={() => onOpen(item)}>
          <strong className="od-suggest-card-name">{item.name}</strong>
          <span className="od-suggest-card-meta">
            {members} member{item.memberCount === 1 ? '' : 's'}
            {item.isStarter ? ' · starter' : ''}
          </span>
        </button>
      </div>
    </article>
  );
}

export function FeedSuggestionsRailSkeleton() {
  return (
    <section className="od-suggest-rail od-suggest-rail--skeleton" aria-hidden>
      <div className="od-suggest-rail-tabs">
        <span className="od-suggest-rail-tab-skel" />
        <span className="od-suggest-rail-tab-skel" />
        <span className="od-suggest-rail-tab-skel" />
      </div>
      <div className="od-suggest-rail-stage">
        <div className="od-suggest-rail-scroller">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="od-suggest-card od-suggest-card--skel" />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FeedSuggestionsRail({
  profile,
  threads = [],
  clubsBrowse = [],
  clubsBrowseLoading = false,
  topClubs = [],
  mostActiveClubs = [],
  topContributors = [],
  joinClubFromBrowse,
  clubBrowseBusy,
  setView,
  addNotification,
}) {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const loggedIn = !!profile?.userId;
  const [activeTab, setActiveTab] = useState('all');
  const [dismissed, setDismissed] = useState(() => new Set());
  const [followStates, setFollowStates] = useState({});
  const [followBusyId, setFollowBusyId] = useState('');
  const [followStatesReady, setFollowStatesReady] = useState(!loggedIn);
  const [discoveredPeople, setDiscoveredPeople] = useState([]);
  const [peoplePoolLoading, setPeoplePoolLoading] = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [starterReady, setStarterReady] = useState(false);
  const followerProfileMode = resolveViewerFollowMode(profile);

  useEffect(() => {
    let cancelled = false;
    loadStarterClubCatalog().then(() => {
      if (!cancelled) setStarterReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const seedPeople = useMemo(
    () =>
      collectSuggestionPeople({
        threads,
        topContributors,
        viewerUserId: profile?.userId,
        loggedIn,
      }),
    [threads, topContributors, profile?.userId, loggedIn]
  );

  useEffect(() => {
    if (!loggedIn || !profile?.userId) {
      setDiscoveredPeople([]);
      setPeoplePoolLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPeoplePoolLoading(true);
    socialApi
      .feedPeopleSuggestions({ limit: 20, followerProfileMode })
      .then((data) => {
        if (!cancelled) setDiscoveredPeople(data?.people || []);
      })
      .catch(() => {
        if (!cancelled) setDiscoveredPeople([]);
      })
      .finally(() => {
        if (!cancelled) setPeoplePoolLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loggedIn, profile?.userId, followerProfileMode]);

  const people = useMemo(
    () =>
      mergeSuggestionPeople(seedPeople, discoveredPeople, {
        viewerUserId: profile?.userId,
      }),
    [seedPeople, discoveredPeople, profile?.userId]
  );

  const clubs = useMemo(
    () => collectSuggestionClubs({ clubsBrowse, topClubs, mostActiveClubs }),
    [clubsBrowse, topClubs, mostActiveClubs, starterReady]
  );

  const peopleKey = people.map((p) => p.userId).join(',');

  useEffect(() => {
    if (!loggedIn || !peopleKey) {
      setFollowStates({});
      setFollowStatesReady(true);
      return undefined;
    }
    let cancelled = false;
    setFollowStatesReady(false);
    (async () => {
      try {
        const idsByMode = new Map();
        for (const person of people) {
          const mode = normalizeProfileMode(person.profileMode);
          if (!idsByMode.has(mode)) idsByMode.set(mode, []);
          idsByMode.get(mode).push(person.userId);
        }
        const batches = await Promise.all(
          [...idsByMode.entries()].map(([followingProfileMode, userIds]) =>
            socialApi.followStatuses({
              userIds,
              followerProfileMode,
              followingProfileMode,
            })
          )
        );
        if (cancelled) return;
        const merged = {};
        for (const data of batches) Object.assign(merged, data.states || {});
        setFollowStates(merged);
      } catch {
        if (!cancelled) setFollowStates({});
      } finally {
        if (!cancelled) setFollowStatesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedIn, peopleKey, people, followerProfileMode]);

  const { suggestedPeople, suggestedClubs, suggestedAll } = useMemo(() => {
    const visiblePeople = people.filter((person) => {
      if (dismissed.has(person.id)) return false;
      if (loggedIn && !followStatesReady) return false;
      const status = followStates[person.userId]?.status;
      return status !== 'active' && status !== 'self';
    });
    const visibleClubs = clubs.filter((club) => !dismissed.has(club.id));
    const all = interleaveSuggestionItems(visiblePeople, visibleClubs, loggedIn);
    return {
      suggestedPeople: visiblePeople,
      suggestedClubs: visibleClubs,
      suggestedAll: all,
    };
  }, [people, clubs, dismissed, followStates, followStatesReady, loggedIn]);

  const displayItems = useMemo(() => {
    if (activeTab === 'people') return suggestedPeople;
    if (activeTab === 'clubs') return suggestedClubs;
    return suggestedAll;
  }, [activeTab, suggestedPeople, suggestedClubs, suggestedAll]);

  const activeTabMeta = SUGGEST_TABS.find((t) => t.id === activeTab) || SUGGEST_TABS[0];
  const hasAnySuggestions = suggestedAll.length > 0;

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      setCanPrev(false);
      setCanNext(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(max - el.scrollLeft > 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, displayItems.length, activeTab]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollLeft = 0;
    updateArrows();
  }, [activeTab, updateArrows]);

  const scrollByCards = useCallback((dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('.od-suggest-card');
    const amount = (card?.getBoundingClientRect().width || 120) + CARD_SCROLL_PAD;
    el.scrollBy({ left: dir * amount * 2, behavior: 'smooth' });
  }, []);

  const goSignIn = useCallback(() => {
    navigate(buildSigninUrl({ returnTo: `${window.location.pathname}${window.location.search}` }));
  }, [navigate]);

  const dismissItem = useCallback((id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleFollow = useCallback(
    async (item) => {
      if (!loggedIn || followBusyId) return;
      setFollowBusyId(item.userId);
      try {
        const data = await socialApi.follow({
          userId: item.userId,
          followerProfileMode,
          followingProfileMode: normalizeProfileMode(item.profileMode),
        });
        setFollowStates((prev) => ({
          ...prev,
          [item.userId]: data.followState || { status: data.status || 'active', isSelf: false },
        }));
        addNotification?.({
          theme: 'green',
          headingText: 'Follow',
          message: data.status === 'pending' ? 'Follow request sent' : `Following ${item.name}`,
          forceClose: false,
          position: 'bottom-right',
        });
        const nextStatus = data.followState?.status || data.status;
        if (nextStatus === 'active') {
          window.setTimeout(() => dismissItem(item.id), 650);
        }
      } catch (err) {
        addNotification?.({
          theme: 'red',
          headingText: 'Follow',
          message: err?.data?.message || err?.message || 'Could not follow',
          forceClose: false,
          position: 'bottom-right',
        });
      } finally {
        setFollowBusyId('');
      }
    },
    [loggedIn, followBusyId, followerProfileMode, addNotification, dismissItem]
  );

  const handleJoin = useCallback(
    async (item) => {
      if (!loggedIn) {
        goSignIn();
        return;
      }
      const ok = await joinClubFromBrowse?.({
        slug: item.slug,
        name: item.name,
        clubId: item.clubId,
      });
      if (ok) dismissItem(item.id);
    },
    [loggedIn, goSignIn, joinClubFromBrowse, dismissItem]
  );

  const waitingForCatalog = !hasAnySuggestions && (clubsBrowseLoading || !starterReady);
  const peopleTabLoading =
    loggedIn && activeTab === 'people' && (peoplePoolLoading || !followStatesReady) && !displayItems.length;
  if (!waitingForCatalog && !hasAnySuggestions && !peopleTabLoading) return null;

  return (
    <section className="od-suggest-rail" aria-label={activeTabMeta.ariaLabel}>
      <div className="od-suggest-rail-tabs" role="tablist" aria-label="Suggestion filters">
        {SUGGEST_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`od-suggest-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-label={tab.ariaLabel}
            className={`od-suggest-rail-tab noOutline-text${activeTab === tab.id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="od-suggest-rail-stage">
        <button
          type="button"
          className="od-suggest-rail-arrow od-suggest-rail-arrow--prev noOutline-text"
          aria-label="Previous suggestions"
          disabled={!canPrev}
          onClick={() => scrollByCards(-1)}
        >
          <FiChevronLeft aria-hidden />
        </button>
        <div
          ref={scrollerRef}
          className="od-suggest-rail-scroller"
          role="tabpanel"
          aria-labelledby={`od-suggest-tab-${activeTab}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              scrollByCards(-1);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              scrollByCards(1);
            }
          }}
        >
          {waitingForCatalog || peopleTabLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={`skel-${i}`} className="od-suggest-card od-suggest-card--skel" aria-hidden />
              ))
            : displayItems.length
              ? displayItems.map((item) =>
                  item.kind === 'person' ? (
                    <SuggestionPersonCard
                      key={item.id}
                      item={item}
                      loggedIn={loggedIn}
                      followState={followStates[item.userId]}
                      followBusy={followBusyId === item.userId}
                      onFollow={handleFollow}
                      onOpen={(person) =>
                        setView?.('user-profile', {
                          userId: person.userId,
                          d: null,
                          feed: '',
                          profileMode: person.profileMode,
                        })
                      }
                      onDismiss={dismissItem}
                      onSignIn={goSignIn}
                    />
                  ) : (
                    <SuggestionClubCard
                      key={item.id}
                      item={item}
                      loggedIn={loggedIn}
                      joinBusy={
                        clubBrowseBusy?.action === 'join' &&
                        clubBrowseBusy?.key === clubBusyKeyFromSlugAndId(item.slug, item.clubId)
                      }
                      onJoin={handleJoin}
                      onOpen={(club) => setView?.('discussion-club', { club: club.slug })}
                      onDismiss={dismissItem}
                      onSignIn={goSignIn}
                    />
                  )
                )
              : (
                  <p className="od-suggest-rail-empty support-muted small">{activeTabMeta.ariaLabel}</p>
                )}
        </div>
        <button
          type="button"
          className="od-suggest-rail-arrow od-suggest-rail-arrow--next noOutline-text"
          aria-label="Next suggestions"
          disabled={!canNext}
          onClick={() => scrollByCards(1)}
        >
          <FiChevronRight aria-hidden />
        </button>
      </div>
    </section>
  );
}
